import dateFormat from 'date-fns/format'

import { getFullName } from '../../utils'

// Pure date / layout helpers for the appointment calendar (Requirements 9.1,
// 9.2, 9.4, 10.2). Nothing in here touches React, SWR or the DOM, so the
// Appointments page (task 11.3) and the appointment form (task 11.4) can reuse
// the week-bounds and range math without rendering a calendar, and every
// function can be unit tested with plain values.
//
// TWO CONVENTIONS ARE INHERITED FROM THE BACKEND AND MUST NOT DRIFT:
//
// 1. THE WEEK RUNS SUNDAY -> SATURDAY (0=Sunday .. 6=Saturday), the same
//    numbering `businessHours.dayOfWeek` uses. GET /appointments/handler/:id/week
//    snaps its `start` param DOWN to that week's Sunday server side
//    (koa/api/appointments/reminderPlanning.js -> WEEK_STARTS_ON / startOfWeek),
//    so a client that assumed Monday would render a grid the response does not
//    line up with.
//
// 2. DATES ARE HANDLED AS 'YYYY-MM-DD' STRINGS, NEVER AS Date OBJECTS.
//    `appointments.date` is a mysql DATE. Serialized through JSON it arrives as
//    something like '2026-02-15T00:00:00.000Z' - midnight UTC. Doing
//    `new Date('2026-02-15T00:00:00.000Z').getDate()` in any negative-UTC-offset
//    timezone (all of the US, i.e. every user of this app) yields the 14th, which
//    would drop appointments into the wrong column of the grid. `toDateOnly`
//    below therefore takes the leading date portion of the string with a REGEX
//    and never lets a timezone conversion near it. Date objects are only built
//    from an already-split y/m/d triple (see `toLocalDate`), which is safe, and
//    only for formatting labels. Date-only strings also compare and sort
//    correctly with plain `<` / `>`, which is what the bounds checks rely on.
//    This mirrors `toDateOnlyString` in koa/api/appointments/overlap.js.

//0=Sunday .. 6=Saturday, matching businessHours.dayOfWeek and the backend's week
export const WEEK_STARTS_ON = 0

export const VIEW_DAY = 'day'
export const VIEW_WEEK = 'week'
export const VIEW_MONTH = 'month'
export const VIEWS = [VIEW_DAY, VIEW_WEEK, VIEW_MONTH]

//Requirement 9.1 - 30 minute slots from 8:00 AM to 8:00 PM
export const SLOT_MINUTES = 30
export const DAY_START_MINUTES = 8 * 60
export const DAY_END_MINUTES = 20 * 60

//Requirement 9.4 - navigation is bounded to 8 weeks ahead / 4 weeks back
export const WEEKS_FORWARD = 8
export const WEEKS_BACK = 4

const pad2 = value => String(value).padStart(2, '0')

// A date-only 'YYYY-MM-DD' string, from a Date, from another date-only string,
// or from the ISO timestamp a mysql DATE turns into over JSON. Anything that is
// not a date returns null so callers can render defensively instead of crashing.
export const toDateOnly = value => {
    if (value instanceof Date) {
        if (isNaN(value.getTime())) return null
        //local getters: a Date built by a picker is already in the user's timezone
        return `${value.getFullYear()}-${pad2(value.getMonth() + 1)}-${pad2(value.getDate())}`
    }
    if (value === null || value === undefined) return null
    //the leading date portion only - any 'T00:00:00.000Z' tail is ignored, never parsed
    const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(value).trim())
    if (!match) return null
    const [, year, month, day] = match
    if (Number(month) < 1 || Number(month) > 12 || Number(day) < 1 || Number(day) > 31) return null
    return `${year}-${month}-${day}`
}

// A Date at local midnight of the given day, built from the split parts so no
// timezone shift can occur. FOR FORMATTING AND getDay() ONLY - all arithmetic
// and comparison happens on the strings.
export const toLocalDate = value => {
    const dateOnly = toDateOnly(value)
    if (!dateOnly) return null
    const [year, month, day] = dateOnly.split('-').map(Number)
    return new Date(year, month - 1, day)
}

//the Date constructor normalizes an out of range day field, so this is correct
//across month, year and DST boundaries
export const addDays = (value, days) => {
    const dateOnly = toDateOnly(value)
    const amount = Number(days)
    if (!dateOnly || !Number.isFinite(amount)) return null
    const [year, month, day] = dateOnly.split('-').map(Number)
    return toDateOnly(new Date(year, month - 1, day + Math.trunc(amount)))
}

//calendar month arithmetic, with the day clamped to the target month's length so
//Jan 31 + 1 month is Feb 28 rather than spilling into March
export const addMonths = (value, months) => {
    const dateOnly = toDateOnly(value)
    const amount = Number(months)
    if (!dateOnly || !Number.isFinite(amount)) return null
    const [year, month, day] = dateOnly.split('-').map(Number)
    const target = new Date(year, month - 1 + Math.trunc(amount), 1)
    const lastDayOfTargetMonth = new Date(target.getFullYear(), target.getMonth() + 1, 0).getDate()
    return toDateOnly(new Date(target.getFullYear(), target.getMonth(), Math.min(day, lastDayOfTargetMonth)))
}

//the Sunday of the week containing `value` (see convention 1 above)
export const startOfWeekDate = (value, weekStartsOn = WEEK_STARTS_ON) => {
    const localDate = toLocalDate(value)
    if (!localDate) return null
    //getDay() is 0=Sunday..6=Saturday, the same numbering as WEEK_STARTS_ON
    return addDays(value, -((localDate.getDay() - weekStartsOn + 7) % 7))
}

export const startOfMonthDate = value => {
    const dateOnly = toDateOnly(value)
    if (!dateOnly) return null
    return `${dateOnly.slice(0, 8)}01`
}

export const endOfMonthDate = value => {
    const dateOnly = toDateOnly(value)
    if (!dateOnly) return null
    const [year, month] = dateOnly.split('-').map(Number)
    //day 0 of the next month is the last day of this one
    return toDateOnly(new Date(year, month, 0))
}

//inclusive list of the days from `start` to `end`
export const eachDay = (start, end) => {
    const first = toDateOnly(start)
    const last = toDateOnly(end)
    if (!first || !last || first > last) return []
    const days = []
    let current = first
    //bounded by construction: `current` strictly increases toward `last`
    while (current && current <= last) {
        days.push(current)
        current = addDays(current, 1)
    }
    return days
}

//accepts a mysql TIME ('HH:MM:SS'), 'HH:MM', or minutes past midnight as a number
export const toMinutes = value => {
    if (typeof value === 'number') return Number.isFinite(value) ? value : null
    if (value === null || value === undefined) return null
    const match = /^(\d{1,2}):(\d{2})(?::(\d{2}))?$/.exec(String(value).trim())
    if (!match) return null
    const hours = Number(match[1])
    const minutes = Number(match[2])
    if (hours > 23 || minutes > 59) return null
    return hours * 60 + minutes
}

//'8:00 AM' / '12:30 PM' - minutes past midnight to a display time
export const formatMinutes = minutes => {
    const total = toMinutes(minutes)
    if (total === null) return ''
    const hours24 = Math.floor(total / 60) % 24
    const hours12 = hours24 % 12 === 0 ? 12 : hours24 % 12
    return `${hours12}:${pad2(total % 60)} ${hours24 < 12 ? 'AM' : 'PM'}`
}

//the row headers of the grid: 8:00 AM, 8:30 AM ... 7:30 PM (Requirement 9.1)
export const timeSlots = () => {
    const slots = []
    for (let minutes = DAY_START_MINUTES; minutes < DAY_END_MINUTES; minutes += SLOT_MINUTES) {
        //an "on the hour" slot carries the label; the half hour row is left blank
        slots.push({ minutes, label: formatMinutes(minutes), isHour: minutes % 60 === 0 })
    }
    return slots
}

export const SLOT_COUNT = Math.round((DAY_END_MINUTES - DAY_START_MINUTES) / SLOT_MINUTES)

// Canonicalize numeric primary keys so a string route ID and a numeric response
// ID identify the same record, while blank, malformed, and unsafe values cannot
// accidentally exclude an unrelated appointment.
const appointmentIdKey = appointmentId => {
    const numericId =
        typeof appointmentId === 'string' && appointmentId.trim() !== '' ? Number(appointmentId) : appointmentId
    return Number.isSafeInteger(numericId) && numericId > 0 ? String(numericId) : null
}

// True when the proposed interval intersects a non-cancelled appointment on the
// same day. `excludedAppointmentId` omits only the appointment currently being
// edited. Strict comparisons deliberately permit exact endpoints: an appointment
// ending at 10:00 AM does not conflict with one beginning at 10:00 AM. An
// unreadable non-cancelled appointment makes that day conservatively unavailable
// rather than allowing a potentially conflicting client selection.
export const hasAppointmentOverlap = (appointments = [], dateOnly, startTime, durationMinutes, excludedAppointmentId) => {
    const day = toDateOnly(dateOnly)
    const proposedStart = toMinutes(startTime)
    const proposedDuration = Number(durationMinutes)
    if (!day || proposedStart === null || !Number.isFinite(proposedDuration) || proposedDuration <= 0) return true

    const excludedId = appointmentIdKey(excludedAppointmentId)
    const proposedEnd = proposedStart + proposedDuration
    return (appointments || []).some(appointment => {
        if (!appointment || toDateOnly(appointment.date) !== day || appointment.status === 'cancelled') return false
        if (excludedId && appointmentIdKey(appointment.id) === excludedId) return false
        const existingStart = toMinutes(appointment.startTime)
        const existingDuration = Number(appointment.durationMinutes)
        if (existingStart === null || !Number.isFinite(existingDuration) || existingDuration <= 0) return true
        const existingEnd = existingStart + existingDuration
        return proposedStart < existingEnd && proposedEnd > existingStart
    })
}

//'30 min' / '1 hr' / '1 hr 30 min' - Requirement 9.2 displays the duration
export const formatDuration = durationMinutes => {
    const minutes = Number(durationMinutes)
    if (!Number.isFinite(minutes) || minutes <= 0) return ''
    const hours = Math.floor(minutes / 60)
    const rest = minutes % 60
    if (!hours) return `${rest} min`
    return rest ? `${hours} hr ${rest} min` : `${hours} hr`
}

// The days a view covers. `start`/`end` are INCLUSIVE date-only strings and are
// exactly what a consumer should send as the `from`/`to` list filters (or as
// `start` for the week endpoint) to fetch the data this range renders.
// For the month view the range is padded out to whole Sunday->Saturday weeks so
// the grid has no ragged rows; `monthStart`/`monthEnd` mark the actual month so
// the leading/trailing days can be dimmed.
export const visibleRange = (view, anchorDate) => {
    const anchor = toDateOnly(anchorDate) || toDateOnly(new Date())
    if (view === VIEW_DAY) {
        return { view, anchor, start: anchor, end: anchor, days: [anchor], monthStart: anchor, monthEnd: anchor }
    }
    if (view === VIEW_MONTH) {
        const monthStart = startOfMonthDate(anchor)
        const monthEnd = endOfMonthDate(anchor)
        const start = startOfWeekDate(monthStart)
        //the Saturday of the week the month ends in
        const end = addDays(startOfWeekDate(monthEnd), 6)
        return { view, anchor, start, end, days: eachDay(start, end), monthStart, monthEnd }
    }
    const start = startOfWeekDate(anchor)
    const end = addDays(start, 6)
    return { view: VIEW_WEEK, anchor, start, end, days: eachDay(start, end), monthStart: start, monthEnd: end }
}

//the seven weeks-of-days rows a month grid renders
export const monthWeeks = range => {
    const days = range && range.days ? range.days : []
    const weeks = []
    for (let index = 0; index < days.length; index += 7) weeks.push(days.slice(index, index + 7))
    return weeks
}

// Requirement 9.4 - the navigable window: 4 weeks back and 8 weeks forward from
// today, snapped out to whole weeks so a bound never lands mid-week and cuts a
// rendered week in half. Inclusive on both ends.
export const navigationBounds = (today = new Date()) => {
    const todayOnly = toDateOnly(today) || toDateOnly(new Date())
    const currentWeekStart = startOfWeekDate(todayOnly)
    return {
        today: todayOnly,
        minDate: addDays(currentWeekStart, -WEEKS_BACK * 7),
        maxDate: addDays(currentWeekStart, WEEKS_FORWARD * 7 + 6),
    }
}

//keeps a date inside the navigable window
export const clampDate = (value, bounds) => {
    const dateOnly = toDateOnly(value)
    if (!dateOnly || !bounds) return dateOnly
    if (bounds.minDate && dateOnly < bounds.minDate) return bounds.minDate
    if (bounds.maxDate && dateOnly > bounds.maxDate) return bounds.maxDate
    return dateOnly
}

//one step of the current view: a day, a week (Requirement 9.4) or a month
export const shiftAnchor = (view, anchorDate, direction) => {
    const step = Number(direction) < 0 ? -1 : 1
    if (view === VIEW_DAY) return addDays(anchorDate, step)
    if (view === VIEW_MONTH) return addMonths(anchorDate, step)
    return addDays(anchorDate, step * 7)
}

// Requirement 9.4 - whether a step is allowed. The step is refused when the
// range it would show falls entirely outside the navigable window, which is what
// disables the control at the bound instead of navigating out of range. The
// anchor is clamped first, so a consumer that hands in an out-of-window date can
// always navigate back toward the window rather than getting stuck.
export const canShift = (view, anchorDate, direction, bounds = navigationBounds()) => {
    const next = shiftAnchor(view, clampDate(anchorDate, bounds), direction)
    if (!next) return false
    const range = visibleRange(view, next)
    if (!range.start || !range.end) return false
    return range.end >= bounds.minDate && range.start <= bounds.maxDate
}

//'Sunday, Feb 15, 2026' / 'Feb 15 - Feb 21, 2026' / 'February 2026'
export const formatRangeLabel = range => {
    if (!range || !range.start) return ''
    const start = toLocalDate(range.start)
    const end = toLocalDate(range.end)
    if (!start || !end) return ''
    if (range.view === VIEW_DAY) return dateFormat(start, 'EEEE, MMM d, yyyy')
    if (range.view === VIEW_MONTH) return dateFormat(toLocalDate(range.monthStart) || start, 'MMMM yyyy')
    const sameYear = start.getFullYear() === end.getFullYear()
    return `${dateFormat(start, sameYear ? 'MMM d' : 'MMM d, yyyy')} \u2013 ${dateFormat(end, 'MMM d, yyyy')}`
}

//'Sun' + '15' for a column header
export const formatDayHeader = value => {
    const localDate = toLocalDate(value)
    if (!localDate) return { weekday: '', day: '' }
    return { weekday: dateFormat(localDate, 'EEE'), day: dateFormat(localDate, 'd') }
}

// Requirement 9.2 - the household name shown on a block. `household` is trimmed
// to {id, familyName, husbandName, wifeName} by every read endpoint, and
// getFullName is the app-wide formatter for that shape; it pads with spaces when
// a name part is missing, so the result is collapsed here.
export const formatHouseholdName = household => {
    if (!household) return ''
    return getFullName(household).replace(/\s+/g, ' ').trim().replace(/,$/, '')
}

// Which relations come back DEPENDS ON THE ENDPOINT: the week endpoint returns
// household + appointmentType, the list endpoint adds handler. A missing
// relation must therefore never crash a block - every formatter below tolerates
// undefined and falls back to a stable placeholder.
export const formatHandlerName = handler => {
    if (!handler) return ''
    return `${handler.firstName || ''} ${handler.lastName || ''}`.replace(/\s+/g, ' ').trim() || handler.email || ''
}

export const formatAppointmentTypeName = appointment => {
    if (!appointment) return ''
    return (appointment.appointmentType && appointment.appointmentType.name) || ''
}

//'9:00 AM - 10:00 AM' (Requirement 9.2 shows the time)
export const formatAppointmentTimeRange = appointment => {
    const startMinutes = appointment ? toMinutes(appointment.startTime) : null
    if (startMinutes === null) return ''
    const duration = Number(appointment.durationMinutes)
    if (!Number.isFinite(duration) || duration <= 0) return formatMinutes(startMinutes)
    return `${formatMinutes(startMinutes)} \u2013 ${formatMinutes(startMinutes + duration)}`
}

// The accessible name of a block: everything Requirement 9.2 / 10.3 puts on it,
// as one readable sentence, because the visual block relies on layout and
// truncation that a screen reader cannot follow. Status is spelled out in words
// so a cancelled appointment is never conveyed by colour alone.
export const describeAppointment = (appointment, { includeHandler = true } = {}) => {
    if (!appointment) return ''
    const localDate = toLocalDate(appointment.date)
    const parts = [
        localDate ? dateFormat(localDate, 'EEEE, MMMM d, yyyy') : '',
        formatAppointmentTimeRange(appointment),
        formatDuration(appointment.durationMinutes),
        formatAppointmentTypeName(appointment),
        formatHouseholdName(appointment.household) || `Household #${appointment.householdId}`,
    ]
    const handlerName = includeHandler ? formatHandlerName(appointment.handler) : ''
    if (handlerName) parts.push(`handled by ${handlerName}`)
    if (appointment.status && appointment.status !== 'scheduled') parts.push(appointment.status)
    return parts.filter(Boolean).join(', ')
}

//the appointments of one day, soonest first. Rows whose date cannot be parsed
//are dropped rather than thrown on: a calendar must render.
export const appointmentsForDay = (appointments = [], dateOnly) => {
    const day = toDateOnly(dateOnly)
    if (!day) return []
    return (appointments || [])
        .filter(appointment => appointment && toDateOnly(appointment.date) === day)
        .sort((a, b) => (toMinutes(a.startTime) || 0) - (toMinutes(b.startTime) || 0))
}

// Requirement 9.2 - each appointment is a BLOCK SPANNING ITS DURATION, so a
// block is positioned and sized in slot units rather than dropped into a single
// cell: `topSlots` is how far down the grid it starts and `spanSlots` how many
// 30 minute rows it covers (both fractional-safe, so a 45 minute appointment or
// one starting at :10 still lands in the right place).
//
// Overlapping appointments (possible across handlers on the list page - the
// server only prevents overlap PER handler) are laid out side by side:
// `columnIndex` / `columnCount` give each block its share of the column width.
//
// Anything falling outside the 8:00 AM - 8:00 PM window has no place in the grid
// and is returned separately in `outside` so the caller can still list it
// instead of silently losing it. A block that merely runs past 8:00 PM is
// clipped to the grid and flagged with `clippedEnd`.
export const layoutDay = (appointments = [], dateOnly) => {
    const blocks = []
    const outside = []

    appointmentsForDay(appointments, dateOnly).forEach(appointment => {
        const startMinutes = toMinutes(appointment.startTime)
        const duration = Number(appointment.durationMinutes)
        if (startMinutes === null || !Number.isFinite(duration) || duration <= 0) {
            outside.push({ appointment, startMinutes, endMinutes: null })
            return
        }
        const endMinutes = startMinutes + duration
        const visibleStart = Math.max(startMinutes, DAY_START_MINUTES)
        const visibleEnd = Math.min(endMinutes, DAY_END_MINUTES)
        if (visibleEnd <= visibleStart) {
            outside.push({ appointment, startMinutes, endMinutes })
            return
        }
        blocks.push({
            appointment,
            startMinutes,
            endMinutes,
            topSlots: (visibleStart - DAY_START_MINUTES) / SLOT_MINUTES,
            spanSlots: (visibleEnd - visibleStart) / SLOT_MINUTES,
            clippedStart: startMinutes < DAY_START_MINUTES,
            clippedEnd: endMinutes > DAY_END_MINUTES,
            columnIndex: 0,
            columnCount: 1,
        })
    })

    // Side by side layout: walk the blocks in start order, keeping a cluster of
    // mutually overlapping ones. A cluster ends as soon as a block starts at or
    // after every earlier block has finished, and every member of a cluster is
    // given the same columnCount so their widths line up.
    let cluster = []
    let clusterEnd = null
    const closeCluster = () => {
        const columnCount = cluster.reduce((max, block) => Math.max(max, block.columnIndex + 1), 1)
        cluster.forEach(block => {
            block.columnCount = columnCount
        })
        cluster = []
        clusterEnd = null
    }

    //ends of the columns currently in use within the open cluster
    let columnEnds = []
    blocks.forEach(block => {
        if (clusterEnd !== null && block.startMinutes >= clusterEnd) {
            closeCluster()
            columnEnds = []
        }
        //reuse the first column that has already finished, else open a new one
        let columnIndex = columnEnds.findIndex(end => end <= block.startMinutes)
        if (columnIndex === -1) columnIndex = columnEnds.length
        columnEnds[columnIndex] = block.endMinutes
        block.columnIndex = columnIndex
        cluster.push(block)
        clusterEnd = clusterEnd === null ? block.endMinutes : Math.max(clusterEnd, block.endMinutes)
    })
    if (cluster.length) closeCluster()

    return { blocks, outside }
}

// --- Jewish holidays -------------------------------------------------------
//
// Fed by GET /appointments/holidays (see useJewishHolidays). Two distinct jobs,
// and keeping them apart is the whole point:
//
//   worksForbidden  -> the day is BLOCKED OUT and cannot be chosen. Yom Tov only.
//   everything else -> the day is LABELLED but stays selectable. Chol HaMoed,
//                      Chanukah, Purim and the fast days are working days; the
//                      user just wants to know before booking.
//
// Saturday is NOT part of this data. It is excluded by the configured business
// hours, which the calendar already reflects, so it is not doubled up here.
//
// Convention 2 above applies: dates are 'YYYY-MM-DD' strings throughout, and
// every lookup runs through toDateOnly so an ISO timestamp from the api and a
// Date from a picker both resolve to the same key.

// The `category` the api assigns each day, most to least significant. Used for
// STYLING only - blocking is driven by `worksForbidden`, never by the category, so
// the two can never disagree about whether a day is bookable.
//
// Chol HaMoed is called out deliberately: it is bookable, but it wants more
// emphasis than an ordinary holiday label.
export const HOLIDAY_YOM_TOV = 'yomTov'
export const HOLIDAY_CHOL_HAMOED = 'cholHaMoed'
export const HOLIDAY_FAST = 'fast'
export const HOLIDAY_EREV = 'erev'
export const HOLIDAY_OTHER = 'holiday'

//date-keyed lookup, so a day cell is O(1) rather than scanning the list
export const holidaysByDate = (holidays = []) => {
    const byDate = {}
    ;(holidays || []).forEach(entry => {
        const date = toDateOnly(entry && entry.date)
        if (!date) return
        byDate[date] = {
            date,
            names: Array.isArray(entry.names) ? entry.names.filter(Boolean) : [],
            category: entry.category || HOLIDAY_OTHER,
            worksForbidden: !!entry.worksForbidden,
            candleLighting: Number.isFinite(Number(entry.candleLighting)) ? Number(entry.candleLighting) : null,
        }
    })
    return byDate
}

export const holidayCategory = (byDate, value) => {
    const holiday = holidayFor(byDate, value)
    return holiday ? holiday.category : null
}

//Chol HaMoed stays selectable but is emphasised more than a plain holiday label
export const isCholHaMoedDay = (byDate, value) => holidayCategory(byDate, value) === HOLIDAY_CHOL_HAMOED

export const holidayFor = (byDate, value) => {
    const date = toDateOnly(value)
    if (!date || !byDate) return null
    return byDate[date] || null
}

// Whether a day must be blocked out of selection. Defaults to FALSE when the
// holiday data is missing or still loading, matching the api, which also allows
// the booking when the calendar is unavailable rather than taking scheduling down.
export const isWorkForbiddenDay = (byDate, value) => {
    const holiday = holidayFor(byDate, value)
    return !!(holiday && holiday.worksForbidden)
}

//'Sukkot I' / 'Chanukah, Rosh Chodesh Tevet' - the label a day cell shows
export const holidayLabel = (byDate, value) => {
    const holiday = holidayFor(byDate, value)
    if (!holiday || !holiday.names.length) return ''
    return holiday.names.join(', ')
}

// The reason a day cannot be chosen, for a tooltip and for the picker's error
// text. Empty string when the day is selectable, so it doubles as the check.
export const workForbiddenReason = (byDate, value) => {
    const holiday = holidayFor(byDate, value)
    if (!holiday || !holiday.worksForbidden) return ''
    const names = holiday.names.join(', ')
    return names
        ? `${names} - appointments cannot be scheduled when work is not permitted`
        : 'Appointments cannot be scheduled on this holiday'
}
